
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { generateCompleteBlogPost } from '@/utils/blogGenerator';
import { BlogPost } from '@/types/blog';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw, Wand, ImageIcon, Check, XCircle, FileText, Images, Lightbulb } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { AspectRatio } from '@/components/ui/aspect-ratio';

interface EnhancedBlogGeneratorProps {
  onBlogGenerated: (blogPost: Partial<BlogPost>) => void;
}

export const EnhancedBlogGenerator: React.FC<EnhancedBlogGeneratorProps> = ({ onBlogGenerated }) => {
  const [topic, setTopic] = useState('');
  const [blogType, setBlogType] = useState<'article' | 'guide' | 'casestudy'>('article');
  const [isGenerating, setIsGenerating] = useState(false);
  const [imageSource, setImageSource] = useState<'huggingface' | 'pexels'>('pexels');
  const [activeTab, setActiveTab] = useState('basic');
  const [targetAudience, setTargetAudience] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Blog type descriptions
  const blogTypeDescriptions = {
    article: "A standard blog article that explores a topic with insights and analysis",
    guide: "A step-by-step tutorial or how-to guide to solve specific problems",
    casestudy: "A detailed analysis of a specific customer implementation and results"
  };

  // Blog structure templates based on type
  const getBlogPromptEnhancement = () => {
    switch(blogType) {
      case 'guide':
        return "Create a comprehensive step-by-step guide with clear instructions, screenshots placeholders, code examples if relevant, and troubleshooting tips.";
      case 'casestudy':
        return "Structure this as a detailed case study with sections for challenge, approach, solution, and measurable results. Include customer quote placeholders.";
      default:
        return "Write this as an informative article with clear sections, engaging examples, and actionable insights.";
    }
  };

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast({
        title: "Topic Required",
        description: "Please enter a blog topic",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const enhancedPrompt = `${topic}
      
Blog Type: ${blogType}
${targetAudience ? `Target Audience: ${targetAudience}` : ''}
      
${getBlogPromptEnhancement()}

Please include image placeholders in markdown format where relevant visuals would enhance the content.
Align tone and style with edgefi brand guidelines - professional, empathetic, and authoritative.`;

      toast({
        title: "Generating Blog Post",
        description: `This may take a minute or two while we create brand-aligned content and find inspiring images using ${imageSource === 'huggingface' ? 'AI generation' : 'Pexels library'}...`,
      });

      const generatedPost = await generateCompleteBlogPost(enhancedPrompt, imageSource);
      
      if (!generatedPost.coverImage) {
        toast({
          title: "Image Generation Issue",
          description: "Blog post was created but we couldn't generate a suitable cover image. You can add one manually.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Blog Post Generated",
          description: generatedPost.imageAttribution?.photographer === "AI Generated" 
            ? "Your AI-generated blog post with AI-generated images is ready!" 
            : "Your AI-generated blog post with brand-aligned images is ready!",
        });
      }
      
      onBlogGenerated(generatedPost);
    } catch (error) {
      console.error("Error generating blog:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to generate blog post";
      setError(errorMessage);
      toast({
        title: "Generation Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const renderExample = () => {
    const examples = {
      article: "The future of IT and cybersecurity management for SMBs in the next 5 years",
      guide: "Implementing Zero Trust Security in Microsoft 365 for Small Businesses: A Step-by-Step Guide",
      casestudy: "How a Regional Healthcare Provider Reduced Security Incidents by 78% with edgefi's Managed Security Services"
    };
    
    return examples[blogType];
  };

  return (
    <Card className="shadow-md border-edgefi-amethyst/20">
      <CardHeader className="bg-gradient-to-r from-edgefi-purple/5 to-transparent border-b border-edgefi-amethyst/10">
        <CardTitle className="flex items-center gap-2 text-edgefi-black">
          <Wand className="h-5 w-5 text-edgefi-purple" />
          Enhanced AI Blog Generator
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid grid-cols-2 mb-4">
                  <TabsTrigger value="basic">Basic</TabsTrigger>
                  <TabsTrigger value="advanced">Advanced</TabsTrigger>
                </TabsList>
                
                <TabsContent value="basic" className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <label htmlFor="topic" className="block text-sm font-medium text-gray-700">
                        Blog Topic
                      </label>
                      <span className="text-xs text-gray-500">Required</span>
                    </div>
                    <Textarea
                      id="topic"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      placeholder={`Enter a blog topic, e.g., '${renderExample()}'`}
                      className="h-32"
                      disabled={isGenerating}
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      The AI will create a complete blog post including title, content, and high-quality images.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Blog Type</label>
                    <RadioGroup 
                      value={blogType}
                      onValueChange={(val) => setBlogType(val as 'article' | 'guide' | 'casestudy')}
                      className="grid grid-cols-1 md:grid-cols-3 gap-2"
                    >
                      <div className={`border rounded-md p-3 cursor-pointer transition-colors ${
                        blogType === 'article' ? 'bg-edgefi-purple/5 border-edgefi-purple' : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                        <RadioGroupItem value="article" id="article" className="sr-only" />
                        <Label 
                          htmlFor="article" 
                          className={`cursor-pointer flex flex-col h-full ${
                            blogType === 'article' ? 'text-edgefi-purple' : ''
                          }`}
                        >
                          <span className="font-medium flex items-center">
                            <FileText className="h-4 w-4 mr-2" />
                            Article
                          </span>
                          <span className="text-xs text-gray-500 mt-1">
                            Insights and analysis on a topic
                          </span>
                        </Label>
                      </div>

                      <div className={`border rounded-md p-3 cursor-pointer transition-colors ${
                        blogType === 'guide' ? 'bg-edgefi-purple/5 border-edgefi-purple' : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                        <RadioGroupItem value="guide" id="guide" className="sr-only" />
                        <Label 
                          htmlFor="guide" 
                          className={`cursor-pointer flex flex-col h-full ${
                            blogType === 'guide' ? 'text-edgefi-purple' : ''
                          }`}
                        >
                          <span className="font-medium flex items-center">
                            <Lightbulb className="h-4 w-4 mr-2" />
                            How-to Guide
                          </span>
                          <span className="text-xs text-gray-500 mt-1">
                            Step-by-step instructions
                          </span>
                        </Label>
                      </div>

                      <div className={`border rounded-md p-3 cursor-pointer transition-colors ${
                        blogType === 'casestudy' ? 'bg-edgefi-purple/5 border-edgefi-purple' : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                        <RadioGroupItem value="casestudy" id="casestudy" className="sr-only" />
                        <Label 
                          htmlFor="casestudy" 
                          className={`cursor-pointer flex flex-col h-full ${
                            blogType === 'casestudy' ? 'text-edgefi-purple' : ''
                          }`}
                        >
                          <span className="font-medium flex items-center">
                            <Images className="h-4 w-4 mr-2" />
                            Case Study
                          </span>
                          <span className="text-xs text-gray-500 mt-1">
                            Customer success story
                          </span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                </TabsContent>

                <TabsContent value="advanced" className="space-y-4">
                  <div>
                    <label htmlFor="audience" className="block text-sm font-medium text-gray-700 mb-2">
                      Target Audience (optional)
                    </label>
                    <Input
                      id="audience"
                      value={targetAudience}
                      onChange={(e) => setTargetAudience(e.target.value)}
                      placeholder="e.g., IT Managers at small healthcare organizations"
                      className="w-full"
                      disabled={isGenerating}
                    />
                  </div>

                  <div className="border rounded-md p-4">
                    <h3 className="text-sm font-medium mb-2 flex items-center">
                      <ImageIcon className="h-4 w-4 mr-1" />
                      Image Source
                    </h3>
                    <RadioGroup 
                      value={imageSource} 
                      onValueChange={(value) => setImageSource(value as 'huggingface' | 'pexels')}
                      className="flex flex-col sm:flex-row gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="huggingface" id="hf" />
                        <Label htmlFor="hf" className="cursor-pointer">
                          <span className="font-medium">AI Generated Images</span>
                          <p className="text-xs text-gray-500">Using HuggingFace models</p>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="pexels" id="pexels" />
                        <Label htmlFor="pexels" className="cursor-pointer">
                          <span className="font-medium">Stock Photos</span>
                          <p className="text-xs text-gray-500">From Pexels library</p>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <div className="hidden md:block border rounded-md p-4 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Example Preview</h3>
              <div className="aspect-video rounded-md bg-white border overflow-hidden shadow-sm">
                <AspectRatio ratio={16/9}>
                  <div className="w-full h-full relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-edgefi-purple/5 to-edgefi-powder-blue/10 flex items-center justify-center">
                      <div className="text-center px-4">
                        <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Example Format</p>
                        <h4 className="font-medium text-sm text-gray-700 mb-2">{renderExample()}</h4>
                        <div className="w-16 h-1 bg-edgefi-purple/30 rounded-full mx-auto"></div>
                      </div>
                    </div>
                  </div>
                </AspectRatio>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {blogTypeDescriptions[blogType]} with edgefi branded styling and relevant imagery.
              </p>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              <p className="font-medium flex items-center">
                <XCircle className="h-4 w-4 mr-1" />
                Error
              </p>
              <p className="mt-1">{error}</p>
              <p className="mt-2 text-xs">Please check if the OpenAI API key is properly configured in Supabase Edge Functions settings.</p>
            </div>
          )}

          <Separator />

          <div className="flex justify-end">
            <Button
              onClick={handleGenerate}
              disabled={isGenerating || !topic.trim()}
              className="bg-edgefi-purple hover:bg-purple-700"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Generating Blog Post...
                </>
              ) : (
                <>
                  <Wand className="mr-2 h-4 w-4" />
                  Generate Complete Blog
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
